import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, Sparkles, LayoutDashboard, Loader2 } from "lucide-react";
import { NAV } from "@/constants/testIds";
import { useAuth } from "@/contexts/AuthContext";
import { createCheckoutSession, formatApiErrorDetail } from "@/lib/api";

export default function Nav({ forceDark = false }) {
  const [scrolled, setScrolled] = useState(
    () => typeof window !== "undefined" && window.scrollY > 24
  );
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(forceDark || window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  async function startCheckout() {
    setBusy(true);
    try {
      const { url } = await createCheckoutSession({});
      window.location.href = url;
    } catch (e) {
      alert(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
      setBusy(false);
    }
  }

  const links = [
    { id: "curriculum", label: "Curriculum", testId: NAV.linkCurriculum },
    { id: "systems", label: "Systems", testId: NAV.linkSystems },
    { id: "about", label: "About", testId: NAV.linkAbout },
    { id: "enroll", label: "Pricing", testId: NAV.linkPricing },
  ];

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "backdrop-blur-2xl bg-black/70 border-b border-cyan-500/15" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 sm:h-20 flex items-center justify-between">
        <button
          data-testid={NAV.brand}
          onClick={() => scrollTo("top")}
          className="flex items-center gap-2.5 group"
        >
          <span className="relative inline-flex items-center justify-center w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-400 to-blue-600 shadow-[0_0_24px_rgba(0,229,255,0.5)] group-hover:shadow-[0_0_32px_rgba(0,229,255,0.85)] transition-shadow">
            <Sparkles className="w-4 h-4 text-black" strokeWidth={2.5} />
          </span>
          <span className="font-display text-white text-lg tracking-tight">
            <span className="font-normal">Favor</span>{" "}
            <span className="font-bold">Studio.</span>
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={l.testId}
              onClick={() => scrollTo(l.id)}
              className="text-sm text-zinc-300 hover:text-cyan-300 transition-colors font-medium"
            >
              {l.label}
            </button>
          ))}
          {user ? (
            <Link
              to="/dashboard"
              data-testid={`${NAV.cta}-dashboard`}
              className="btn-aurora !py-2.5 !px-5 !text-xs inline-flex items-center gap-2"
            >
              <LayoutDashboard className="w-3.5 h-3.5" /> DASHBOARD
            </Link>
          ) : (
            <>
              <Link
                to="/login"
                data-testid="nav-login-link"
                className="text-sm text-zinc-300 hover:text-cyan-300 transition-colors font-medium"
              >
                Login
              </Link>
              <button
                data-testid={NAV.cta}
                onClick={startCheckout}
                disabled={busy}
                className="btn-aurora !py-2.5 !px-5 !text-xs disabled:opacity-70"
              >
                {busy ? (<><Loader2 className="w-3.5 h-3.5 animate-spin" /> LOADING…</>) : "ENROLL — $497"}
              </button>
            </>
          )}
        </nav>

        <button
          data-testid={NAV.mobileToggle}
          onClick={() => setOpen((v) => !v)}
          className="md:hidden text-white p-2 rounded-lg border border-cyan-500/20 bg-black/40"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-cyan-500/15 bg-black/95 backdrop-blur-2xl">
          <div className="px-5 py-6 flex flex-col gap-5">
            {links.map((l) => (
              <button
                key={l.id}
                data-testid={`${l.testId}-mobile`}
                onClick={() => scrollTo(l.id)}
                className="text-left text-zinc-200 hover:text-cyan-300 transition-colors font-medium"
              >
                {l.label}
              </button>
            ))}
            {user ? (
              <Link
                to="/dashboard"
                onClick={() => setOpen(false)}
                data-testid={`${NAV.cta}-dashboard-mobile`}
                className="btn-aurora w-full inline-flex items-center justify-center gap-2"
              >
                <LayoutDashboard className="w-4 h-4" /> DASHBOARD
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  onClick={() => setOpen(false)}
                  data-testid="nav-login-link-mobile"
                  className="text-left text-zinc-200 hover:text-cyan-300 transition-colors font-medium"
                >
                  Login
                </Link>
                <button
                  data-testid={`${NAV.cta}-mobile`}
                  onClick={() => { setOpen(false); startCheckout(); }}
                  disabled={busy}
                  className="btn-aurora w-full"
                >
                  {busy ? "LOADING…" : "ENROLL — $497"}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
